import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export type PublishErrorPresentation = {
  displayMessage: string
  technicalMessage: string | null
  kind: 'socks_proxy_missing' | 'generic'
}

export type HighRiskConfirmDialogContent = {
  title: string
  message: string
  confirmLabel: string
}

type PhoneValidationErrorKey = 'phoneRequired' | 'phoneInvalid'
type OtpValidationErrorKey = 'invalidCode'
type PasswordValidationErrorKey = 'passwordRequired' | 'passwordTooShort' // pragma: allowlist secret
type PasswordStrengthLevelKey = 'resetStrengthWeak' | 'resetStrengthMedium' | 'resetStrengthStrong' // pragma: allowlist secret
type PasswordRequirementLabelKey = 'required' | 'minLength' // pragma: allowlist secret

export type PasswordRequirementState = {
  key: PasswordRequirementLabelKey
  met: boolean
}

type BuildHighRiskConfirmDialogOptions = {
  actionLabel: string
  targetLabel: string
  irreversibleLabel: string
  titleTemplate: string
  targetPrefix: string
  irreversiblePrefix: string
  continueHint: string
}

type AuthMessageResolver = (key: string, params?: Record<string, string | number>) => string

type ResolvePublishErrorOptions = {
  socksFriendlyMessage: string
  fallbackMessage?: string
}

type CoerceNumberWithGuardrailsOptions = {
  rawValue: number | string | null | undefined
  min?: number
  max?: number
  fallback?: number
}

export type NumberGuardrailReason = 'invalid' | 'below_min' | 'above_max'

export type NumberGuardrailResult = {
  value: number
  corrected: boolean
  reason: NumberGuardrailReason | null
  min: number
  max: number | null
}

export const AUTH_PHONE_LENGTH = 11
export const AUTH_OTP_LENGTH = 6
export const AUTH_PASSWORD_MIN_LENGTH = 8

const AUTH_ERROR_CODE_MESSAGE_KEY_MAP: Record<string, string> = {
  TERMS_REQUIRED: 'authErrorTermsRequired',
  INVALID_TOKEN: 'authErrorInvalidToken',
  TOKEN_EXPIRED: 'authErrorTokenExpired',
  TOKEN_REVOKED: 'authErrorTokenRevoked',
  UNAUTHORIZED: 'authErrorUnauthorized',
  FORBIDDEN: 'authErrorForbidden',
  USER_NOT_FOUND: 'authErrorUserNotFound',
  USER_DISABLED: 'authErrorUserDisabled',
  INVALID_CREDENTIALS: 'authErrorInvalidCredentials',
  MOBILE_NUMBER_ILLEGAL: 'authErrorPhoneInvalid',
  ISV_MOBILE_NUMBER_ILLEGAL: 'authErrorPhoneInvalid',
  FREQUENCY_FAIL: 'authErrorSmsRateLimited',
  BIZ_FREQUENCY: 'authErrorSmsRateLimited',
  BUSINESS_LIMIT_CONTROL: 'authErrorSmsDailyLimit',
  ISV_DAY_LIMIT_CONTROL: 'authErrorSmsDailyLimit',
  ISV_BUSINESS_LIMIT_CONTROL: 'authErrorSmsDailyLimit',
  FUNCTION_NOT_OPENED: 'authErrorSmsServiceUnavailable',
  ISV_SMS_TEMPLATE_ILLEGAL: 'authErrorSmsServiceUnavailable',
  ISV_AMOUNT_NOT_ENOUGH: 'authErrorSmsServiceUnavailable'
}

const normalizeAuthErrorCode = (value: string): string =>
  value
    .trim()
    .replace(/[\s.-]+/g, '_')
    .toUpperCase()

const extractAuthErrorCode = (record: Record<string, unknown>): string => {
  const detail = record.detail
  const detailRecord = detail && typeof detail === 'object' ? (detail as Record<string, unknown>) : null
  const candidateCodes = [
    record.code,
    record.error_code,
    record.errorCode,
    detailRecord?.code,
    detailRecord?.error_code,
    detailRecord?.errorCode
  ]
  for (const candidate of candidateCodes) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim()
    }
  }
  return ''
}

const resolveMappedAuthErrorMessage = (
  code: string,
  fallbackMessage: string,
  resolveMessage?: AuthMessageResolver
): string | null => {
  const normalizedCode = normalizeAuthErrorCode(code)
  const messageKey = AUTH_ERROR_CODE_MESSAGE_KEY_MAP[normalizedCode]
  if (!messageKey) {
    console.warn('[auth] 未映射的认证错误码:', code)
    return fallbackMessage
  }
  if (!resolveMessage) {
    return fallbackMessage
  }
  const translatedMessage = resolveMessage(messageKey)
  if (translatedMessage && translatedMessage !== messageKey) {
    return translatedMessage
  }
  return fallbackMessage
}

const normalizeUnknownErrorText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim()
  }
  if (!value || typeof value !== 'object') {
    return ''
  }
  const data = value as Record<string, unknown>
  const directMessage = data.message
  if (typeof directMessage === 'string') {
    return directMessage.trim()
  }
  const detail = data.detail
  if (typeof detail === 'string') {
    return detail.trim()
  }
  if (detail && typeof detail === 'object') {
    const detailObj = detail as Record<string, unknown>
    if (typeof detailObj.message === 'string') {
      return detailObj.message.trim()
    }
    if (typeof detailObj.error === 'string') {
      return detailObj.error.trim()
    }
  }
  if (typeof data.error === 'string') {
    return data.error.trim()
  }
  return ''
}

const normalizeGuardrailBoundary = (value: unknown, fallback: number): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export const coerceNumberWithGuardrails = (
  options: CoerceNumberWithGuardrailsOptions
): NumberGuardrailResult => {
  const min = normalizeGuardrailBoundary(options.min, 0)
  const rawMax = normalizeGuardrailBoundary(options.max, Number.NaN)
  const max = Number.isFinite(rawMax) ? Math.max(rawMax, min) : null
  const fallbackRaw = normalizeGuardrailBoundary(options.fallback, min)
  const fallback = max === null
    ? Math.max(fallbackRaw, min)
    : Math.min(max, Math.max(fallbackRaw, min))
  const parsedValue = typeof options.rawValue === 'number'
    ? options.rawValue
    : Number(options.rawValue)

  if (!Number.isFinite(parsedValue)) {
    return {
      value: fallback,
      corrected: true,
      reason: 'invalid',
      min,
      max
    }
  }

  if (parsedValue < min) {
    return {
      value: min,
      corrected: true,
      reason: 'below_min',
      min,
      max
    }
  }

  if (max !== null && parsedValue > max) {
    return {
      value: max,
      corrected: true,
      reason: 'above_max',
      min,
      max
    }
  }

  return {
    value: parsedValue,
    corrected: false,
    reason: null,
    min,
    max
  }
}

export const resolvePublishErrorPresentation = (
  rawError: unknown,
  options: ResolvePublishErrorOptions
): PublishErrorPresentation => {
  const rawText = normalizeUnknownErrorText(rawError)
  const lowered = rawText.toLowerCase()
  const isSocksProxyError = lowered.includes('socksio') || lowered.includes('socks proxy')
  if (isSocksProxyError) {
    return {
      displayMessage: options.socksFriendlyMessage,
      technicalMessage: rawText || null,
      kind: 'socks_proxy_missing'
    }
  }
  const fallback = (options.fallbackMessage || '').trim()
  if (!rawText && fallback) {
    return {
      displayMessage: fallback,
      technicalMessage: null,
      kind: 'generic'
    }
  }
  return {
    displayMessage: rawText || fallback || options.socksFriendlyMessage,
    technicalMessage: rawText && rawText !== fallback ? rawText : null,
    kind: 'generic'
  }
}

export const buildHighRiskConfirmDialog = (
  options: BuildHighRiskConfirmDialogOptions
): HighRiskConfirmDialogContent => {
  const actionLabel = options.actionLabel.trim()
  const targetLabel = options.targetLabel.trim()
  const irreversibleLabel = options.irreversibleLabel.trim()
  const title = options.titleTemplate.replace('{action}', actionLabel)

  return {
    title,
    confirmLabel: actionLabel,
    message: [
      `${options.targetPrefix}${targetLabel}`,
      `${options.irreversiblePrefix}${irreversibleLabel}`,
      options.continueHint.trim()
    ]
      .filter((line) => line.trim().length > 0)
      .join('\n')
  }
}

export const normalizeAuthPhone = (value: string): string => value.replace(/[^\d]+/g, '')

export const getPhoneValidationErrorKey = (value: string): PhoneValidationErrorKey | null => {
  const normalizedPhone = normalizeAuthPhone(value)
  if (!normalizedPhone) {
    return 'phoneRequired'
  }
  if (normalizedPhone.length !== AUTH_PHONE_LENGTH) {
    return 'phoneInvalid'
  }
  return null
}

export const getOtpValidationErrorKey = (value: string): OtpValidationErrorKey | null => {
  const normalizedCode = value.trim()
  if (!normalizedCode || normalizedCode.length !== AUTH_OTP_LENGTH) {
    return 'invalidCode'
  }
  return null
}

export const getPasswordValidationErrorKey = (value: string): PasswordValidationErrorKey | null => {
  if (!value) {
    return 'passwordRequired'
  }
  if (value.length < AUTH_PASSWORD_MIN_LENGTH) {
    return 'passwordTooShort'
  }
  return null
}

export const getPasswordRequirementStates = (value: string): PasswordRequirementState[] => [
  {
    key: 'required',
    met: value.length > 0
  },
  {
    key: 'minLength',
    met: value.length >= AUTH_PASSWORD_MIN_LENGTH
  }
]

export const getPasswordStrengthScore = (value: string): number => {
  let score = 0
  if (value.length >= AUTH_PASSWORD_MIN_LENGTH) {
    score += 1
  }
  if (/[A-Za-z]/.test(value)) {
    score += 1
  }
  if (/\d/.test(value)) {
    score += 1
  }
  if (/[^A-Za-z0-9]/.test(value)) {
    score += 1
  }
  return Math.min(score, 4)
}

export const getPasswordStrengthLevelKey = (score: number): PasswordStrengthLevelKey => {
  if (score >= 3) {
    return 'resetStrengthStrong'
  }
  if (score >= 2) {
    return 'resetStrengthMedium'
  }
  return 'resetStrengthWeak'
}

export const resolveAuthApiErrorMessage = (
  payload: unknown,
  fallbackMessage: string,
  resolveMessage?: AuthMessageResolver
): string => {
  if (!payload || typeof payload !== 'object') {
    return fallbackMessage
  }

  const record = payload as Record<string, unknown>
  const code = extractAuthErrorCode(record)
  if (code) {
    return resolveMappedAuthErrorMessage(code, fallbackMessage, resolveMessage) ?? fallbackMessage
  }
  const detail = record.detail
  if (typeof detail === 'string') {
    return detail
  }
  if (detail && typeof detail === 'object') {
    const detailRecord = detail as Record<string, unknown>
    if (typeof detailRecord.message === 'string') {
      return detailRecord.message
    }
    if (typeof detailRecord.error === 'string') {
      return detailRecord.error
    }
  }
  if (typeof record.message === 'string') {
    return record.message
  }
  if (typeof record.error === 'string') {
    return record.error
  }
  return fallbackMessage
}

export type LogMessagePart = {
  text: string
  highlight: boolean
}

const DEFAULT_LOG_KEYWORDS = ['ERROR', 'CACHE', 'THINK']

export function splitLogMessageParts(message: string, keywords: string[] = DEFAULT_LOG_KEYWORDS): LogMessagePart[] {
  if (!message) {
    return []
  }
  const normalized = String(message)
  if (!keywords.length) {
    return [{ text: normalized, highlight: false }]
  }
  const escaped = keywords.map((keyword) => keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi')
  const parts = normalized.split(regex).filter((part) => part.length > 0)
  const loweredKeywords = keywords.map((keyword) => keyword.toLowerCase())
  return parts.map((part) => ({
    text: part,
    highlight: loweredKeywords.includes(part.toLowerCase())
  }))
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string
      reset: (widgetId?: string) => void
      remove: (widgetId?: string) => void
    }
  }
}

let turnstileScriptPromise: Promise<void> | null = null

export function loadTurnstileScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('turnstile script can only load in browser'))
  }

  if (window.turnstile) {
    return Promise.resolve()
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-turnstile-script="true"]') as HTMLScriptElement | null
    if (existing) {
      if (window.turnstile) {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('turnstile script load failed')))
      return
    }

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.dataset.turnstileScript = 'true'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('turnstile script load failed'))
    document.head.appendChild(script)
  })

  return turnstileScriptPromise
}

export function resolveAuthToken(storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage) {
  if (!storage?.getItem) {
    return null
  }

  const directKeys = ['token', 'access_token', 'auth_token', 'authToken', 'ozon_token', 'guest_token', 'guestToken']
  for (const key of directKeys) {
    const value = storage.getItem(key)
    if (value) {
      return value
    }
  }

  const authPayload = storage.getItem('auth')
  if (authPayload) {
    try {
      const parsed = JSON.parse(authPayload)
      const token = parsed?.token || parsed?.accessToken || parsed?.access_token
      if (typeof token === 'string' && token) {
        return token
      }
    } catch (error) {
      return null
    }
  }

  return null
}

type ExtensionSendMessage = (
  extensionId: string,
  payload: Record<string, any>,
  cb?: (response?: any) => void
) => void

function getExtensionRuntimeBridge() {
  if (typeof window === 'undefined') {
    return null
  }
  const extensionId = document.documentElement?.getAttribute('data-ozon-erp-extension-id')
  const chromeRuntime = (window as unknown as { chrome?: { runtime?: { sendMessage?: Function } } }).chrome?.runtime
  const sendMessage = chromeRuntime?.sendMessage as ExtensionSendMessage | undefined
  if (!extensionId || !sendMessage) {
    return null
  }
  return {
    extensionId,
    sendMessage,
  }
}

async function sendMessageToExtension(payload: Record<string, any>) {
  const bridge = getExtensionRuntimeBridge()
  if (!bridge) {
    return null
  }

  return await new Promise<any | null>((resolve) => {
    try {
      bridge.sendMessage(bridge.extensionId, payload, (response) => {
        resolve(response ?? null)
      })
    } catch (error) {
      resolve(null)
    }
  })
}

export async function syncAuthToExtension(
  token: string,
  user: Record<string, any> | null,
  apiBaseUrl?: string
) {
  if (!token || typeof window === 'undefined') {
    return false
  }

  const apiUrl = resolveApiBaseUrl(apiBaseUrl)
  const response = await sendMessageToExtension({
    action: 'syncAuth',
    token,
    user,
    apiUrl,
  })

  if (response?.success) {
    window.dispatchEvent(new Event('extension:refresh-status'))
    window.postMessage({ type: 'OZON_ERP_REQUEST_STATUS' }, '*')
    return true
  }

  return false
}

export async function logoutExtensionSession() {
  if (typeof window === 'undefined') {
    return false
  }

  const response = await sendMessageToExtension({ action: 'logout' })
  if (response?.success) {
    window.dispatchEvent(new Event('extension:refresh-status'))
    window.postMessage({ type: 'OZON_ERP_REQUEST_STATUS' }, '*')
    return true
  }
  return false
}

export async function requestExtensionStatus() {
  if (typeof window === 'undefined') {
    return null
  }

  const response = await sendMessageToExtension({ action: 'getStatus' })
  if (!response || response.success === false) {
    return null
  }
  return response
}

export async function performLogout(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null = typeof localStorage === 'undefined'
    ? null
    : localStorage,
  apiBaseUrl?: string
) {
  if (!storage?.getItem) {
    return
  }
  const token = resolveAuthToken(storage)
  if (token && typeof fetch !== 'undefined') {
    try {
      const baseUrl = resolveApiBaseUrl(apiBaseUrl)
      await fetch(`${baseUrl}/api/v1/auth/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    } catch (error) {
      // ignore logout errors
    }
  }

  clearAuthSession(storage)
}

const AUTH_SESSION_STORAGE_KEYS = [
  'token',
  'access_token',
  'auth_token',
  'authToken',
  'ozon_token',
  'guest_token',
  'guestToken',
  'auth',
  'guest_user',
] as const

export function clearAuthSession(
  storage: Pick<Storage, 'removeItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.removeItem) {
    return
  }
  for (const key of AUTH_SESSION_STORAGE_KEYS) {
    try {
      storage.removeItem(key)
    } catch (error) {
      // ignore storage errors
    }
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth:updated'))
  }
}

const ADMIN_TOKEN_KEY = 'admin_token'

export function resolveAdminToken(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.getItem) {
    return null
  }
  return storage.getItem(ADMIN_TOKEN_KEY)
}

export function saveAdminToken(
  token: string,
  storage: Pick<Storage, 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.setItem) {
    return
  }
  try {
    storage.setItem(ADMIN_TOKEN_KEY, token)
  } catch (error) {
    // ignore storage errors
  }
}

export function clearAdminToken(
  storage: Pick<Storage, 'removeItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.removeItem) {
    return
  }
  try {
    storage.removeItem(ADMIN_TOKEN_KEY)
  } catch (error) {
    // ignore storage errors
  }
}

export type AuthUserPayload = {
  id?: string
  phone?: string
  email?: string
  avatar?: string | null
  plan?: string
  plan_name?: string
  status?: string
  is_admin?: boolean
  nickname?: string
  username?: string
}

export type AuthPayload = {
  token?: string
  user?: AuthUserPayload
  quota?: Record<string, unknown>
}

export function resolveAuthPayload(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.getItem) {
    return null
  }
  const raw = storage.getItem('auth')
  if (!raw) {
    return null
  }
  try {
    return JSON.parse(raw) as AuthPayload
  } catch (error) {
    return null
  }
}

export function resolveAuthUser(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  const payload = resolveAuthPayload(storage)
  if (payload?.user) {
    return payload.user
  }
  if (!storage?.getItem) {
    return null
  }
  const guestRaw = storage.getItem('guest_user')
  if (!guestRaw) {
    return null
  }
  try {
    const parsed = JSON.parse(guestRaw)
    if (parsed && typeof parsed === 'object') {
      return parsed as AuthUserPayload
    }
  } catch (error) {
    return null
  }
  return null
}

export async function refreshAuthProfile(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null = typeof localStorage === 'undefined'
    ? null
    : localStorage,
  apiBaseUrl?: string
) {
  if (!storage?.getItem || typeof fetch === 'undefined') {
    return null
  }
  const token = resolveAuthToken(storage)
  if (!token) {
    return null
  }
  const baseUrl = resolveApiBaseUrl(apiBaseUrl)
  try {
    const response = await fetch(`${baseUrl}/api/v1/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    if (!response.ok) {
      return null
    }
    const payload = await response.json().catch(() => null)
    if (!payload?.user) {
      return null
    }
    const existing = resolveAuthPayload(storage) ?? {}
    const nextPayload = {
      ...existing,
      ...payload,
      token: existing?.token || token,
      user: payload.user,
      quota: payload.quota ?? existing?.quota
    }
    storage.setItem('auth', JSON.stringify(nextPayload))
    return nextPayload
  } catch (error) {
    return null
  }
}

export function buildAuthModalPath(
  pathname: string,
  search: string,
  mode: 'login' | 'signup' | 'forgot' | 'reset'
) {
  const params = new URLSearchParams(search)
  params.set('auth', mode)
  const nextSearch = params.toString()
  return `${pathname}${nextSearch ? `?${nextSearch}` : ''}`
}

export type UserStage = {
  stage: 'guest' | 'new' | 'active'
  isAuthenticated: boolean
  plan: string
  isAdmin: boolean
}

export function resolveUserStage(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
): UserStage {
  const token = resolveAuthToken(storage)
  if (!token) {
    return {
      stage: 'guest',
      isAuthenticated: false,
      plan: 'free',
      isAdmin: false
    }
  }

  const user = resolveAuthUser(storage)
  const plan = user?.plan || 'free'
  const isAdmin = Boolean(user?.is_admin)
  const stage = plan && plan !== 'free' ? 'active' : 'new'
  return {
    stage,
    isAuthenticated: true,
    plan,
    isAdmin
  }
}

export const IMAGE_CREDITS_PER_IMAGE = 5
export const CREDITS_PER_DOLLAR = 100

export type CreditPackId = 'basic' | 'growth' | 'scale'

export type CreditPackPreset = {
  id: CreditPackId
  credits: number
  priceUsd: number
  bonusPercent: number
  highlight?: boolean
}

export const CREDIT_PACK_PRESETS: CreditPackPreset[] = [
  { id: 'basic', credits: 1000, priceUsd: 10, bonusPercent: 0 },
  { id: 'growth', credits: 3000, priceUsd: 27, bonusPercent: 10, highlight: true },
  { id: 'scale', credits: 8000, priceUsd: 68, bonusPercent: 15 }
]

export type SubscriptionPlanId = 'starter' | 'pro' | 'business'

export type SubscriptionPlanPreset = {
  id: SubscriptionPlanId
  monthlyUsd: number
  yearlyUsd: number
  creditsMonthly: number | null
  highlight?: boolean
}

export const SUBSCRIPTION_PLAN_PRESETS: SubscriptionPlanPreset[] = [
  { id: 'starter', monthlyUsd: 19, yearlyUsd: 190, creditsMonthly: 2000 },
  { id: 'pro', monthlyUsd: 49, yearlyUsd: 490, creditsMonthly: 5600, highlight: true },
  { id: 'business', monthlyUsd: 99, yearlyUsd: 990, creditsMonthly: 12000 }
]

export function creditsToImages(credits?: number | null) {
  if (!credits) {
    return 0
  }
  return Math.floor(credits / IMAGE_CREDITS_PER_IMAGE)
}

export const formatAdminQuantity = (value: number | string | null | undefined, unit = '') => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return unit ? `0 ${unit}` : '0'
  }
  const text = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  }).format(parsed)
  return unit ? `${text} ${unit}` : text
}

export const formatAdminMoney = (value: number | string | null | undefined, currency = '') => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return currency ? `${currency} 0.00` : '0.00'
  }

  const normalized = String(currency || '').trim()
  if (!normalized) {
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(parsed)
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: normalized.toUpperCase(),
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(parsed)
  } catch (_error) {
    const text = new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(Math.abs(parsed))
    const sign = parsed < 0 ? '-' : ''
    return `${sign}${normalized} ${text}`
  }
}

export function buildCreditsSummary(credits: number, creditsLabel = 'Credits', imagesLabel = 'Images') {
  const images = creditsToImages(credits)
  return `${credits} ${creditsLabel} · ${images} ${imagesLabel}`
}

export function isInsufficientCreditsMessage(value: unknown) {
  if (!value) {
    return false
  }
  const text = String(value)
  return /积分不足|余额不足|insufficient|not enough credits|quota_exhausted|recharge/i.test(text)
}

export function extractCreditsNumbers(message: string): { needed?: number; current?: number } | null {
  const text = String(message || '')
  if (!text) {
    return null
  }

  const patterns: Array<RegExp> = [
    /需要\s*(\d+)\s*积分[\s\S]*?(?:当前|现有|余额)\s*(\d+)\s*积分/i,
    /(?:当前|现有|余额)\s*[:：]?\s*(\d+)\s*积分[\s\S]*?需要\s*[:：]?\s*(\d+)\s*积分/i,
    /当前\s*(\d+)\s*积分\s*,?\s*需要\s*(\d+)\s*积分/i,
  ]

  for (const pattern of patterns) {
    const match = text.match(pattern)
    if (!match) {
      continue
    }
    if (pattern === patterns[1] || pattern === patterns[2]) {
      const current = Number(match[1])
      const needed = Number(match[2])
      return {
        current: Number.isFinite(current) ? current : undefined,
        needed: Number.isFinite(needed) ? needed : undefined,
      }
    }
    const needed = Number(match[1])
    const current = Number(match[2])
    return {
      needed: Number.isFinite(needed) ? needed : undefined,
      current: Number.isFinite(current) ? current : undefined,
    }
  }

  return null
}

export type AgentStatus = 'IDLE' | 'THINKING' | 'ACTIVE' | 'COMPLETE' | 'ERROR'

export function resolveStudioCollectionId(state: unknown, search: string) {
  if (state && typeof state === 'object' && 'collectionId' in state) {
    const value = (state as { collectionId?: unknown }).collectionId
    if (typeof value === 'string' && value) {
      return value
    }
  }

  const params = new URLSearchParams(search)
  return params.get('collectionId') || params.get('collection_id') || ''
}

export function mapStageStatusToAgentStatus(status?: string): AgentStatus {
  if (!status) {
    return 'IDLE'
  }

  if (status === 'completed' || status === 'skipped') {
    return 'COMPLETE'
  }

  if (status === 'failed') {
    return 'ERROR'
  }

  if (status === 'running') {
    return 'ACTIVE'
  }

  return 'IDLE'
}

export type StageStatusMap = Record<string, {
  status?: string
  execution_time_ms?: number
  error?: string
  updated_at?: string
  runtime_mode?: 'foreground' | 'background' | string
  running_seconds?: number
  soft_timeout_at?: string | null
  hard_timeout_at?: string | null
}>

export function toStageStatusMap(response?: { stages?: StageStatusMap | null }) {
  return response?.stages ?? {}
}

export type ImagePlan = {
  index?: number
  prompt?: string
  overlay_text?: string
  constraint_suffix?: string
  generated_url?: string
  generated_raw_url?: string
  generated_at?: string
  generated_version?: string | number
  candidate?: {
    status?: string
    raw_url?: string
    url?: string
    generated_at?: string
    version?: string | number
  } | null
  has_pending_candidate?: boolean
  history?: Array<{
    status?: string
    url?: string
    raw_url?: string
    recorded_at?: string
    version?: string | number
  }>
  image_type?: string
  aspect_ratio?: string
  keywords?: string[]
  scene?: string
}

export function normalizeImagePlans(payload?: { image_plans?: ImagePlan[] | null }) {
  const plans = payload?.image_plans ?? []
  if (!Array.isArray(plans) || plans.length === 0) {
    return []
  }
  return plans
    .filter((plan): plan is ImagePlan => Boolean(plan && typeof plan === 'object'))
    .map((plan) => {
      const next: ImagePlan = { ...plan }
      if (typeof next.prompt === 'string') {
        next.prompt = next.prompt
          .replace(/(?:,\s*or\s*)?promotional\s+text\.?/gi, '')
          .replace(/\s+\./g, '.')
          .replace(/,\s*\./g, '.')
          .replace(/\.\s*\./g, '.')
          .replace(/\s{2,}/g, ' ')
          .trim()
      }
      if (typeof next.constraint_suffix === 'string') {
        next.constraint_suffix = next.constraint_suffix
          .replace(/(?:,\s*or\s*)?promotional\s+text\.?/gi, '')
          .replace(/\s+\./g, '.')
          .replace(/,\s*\./g, '.')
          .replace(/\.\s*\./g, '.')
          .replace(/\s{2,}/g, ' ')
          .trim()
      }
      return next
    })
}

export function buildGeneratePayload(input: {
  marketStatus?: string
  skipImages?: boolean
  useCache?: boolean
  fromStage?: string
  capability?: CapabilityProfile
  storeId?: number | null
}) {
  return {
    market_status: input.marketStatus ?? '中等竞争',
    skip_images: input.skipImages ?? false,
    use_cache: input.useCache ?? false,
    from_stage: input.fromStage ?? null,
    store_id: input.storeId ?? null,
    capability: input.capability ?? loadCapabilityProfile()
  }
}

export type CapabilityStrategyTier = 'economy' | 'balanced' | 'premium'

export interface CapabilityProfile {
  platform: 'ozon' | 'amazon' | 'shopify' | 'aliexpress' | 'ebay'
  market_region:
  | 'ru'
  | 'us'
  | 'uk'
  | 'de'
  | 'fr'
  | 'it'
  | 'es'
  | 'jp'
  | 'br'
  | 'ca'
  | 'mx'
  | 'au'
  | 'sg'
  | 'in'
  | 'tr'
  | 'ae'
  | 'sa'
  | 'nl'
  | 'se'
  | 'pl'
  | 'be'
  | 'ie'
  | 'za'
  | 'eg'
  | 'global'
  strategy_tier: CapabilityStrategyTier
  image_max_images?: number | null
  brand_guard: boolean
  language:
  | 'ru'
  | 'en'
  | 'de'
  | 'fr'
  | 'it'
  | 'es'
  | 'ja'
  | 'pt-br'
  | 'pt-pt'
  | 'nl'
  | 'sv'
  | 'pl'
  | 'tr'
  | 'ar'
  | 'zh-hans'
  | 'zh-hant'
  | 'cs'
  | 'da'
  | 'fi'
  | 'ko'
  | 'nb'
  | 'th'
  content_style: 'professional' | 'creative' | 'concise' | 'custom'
  custom_prompt: string
  seo_level: 'standard' | 'deep'
}

export interface CapabilityContext extends CapabilityProfile {
  image_generation: {
    enabled: boolean
    max_images: number
    style_preset: string
  }
  rich_content: {
    enabled: boolean
    theme: string
  }
  model_preset: {
    text: string
    image: string
  }
  summary: string[]
}

const CAPABILITY_STORAGE_KEY = 'capability_profile'
const CAPABILITY_SETTINGS_SKIP_STORAGE_KEY = 'capability_settings_skip'

export type CapabilityPreferences = {
  capability_settings_skip: boolean
}

export const defaultCapabilityProfile: CapabilityProfile = {
  platform: 'ozon',
  market_region: 'ru',
  strategy_tier: 'balanced',
  image_max_images: 2,
  brand_guard: true,
  language: 'ru',
  content_style: 'professional',
  custom_prompt: '',
  seo_level: 'deep'
}

export function loadCapabilityProfile(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
): CapabilityProfile {
  if (!storage?.getItem) {
    return { ...defaultCapabilityProfile }
  }
  const raw = storage.getItem(CAPABILITY_STORAGE_KEY)
  if (!raw) {
    return { ...defaultCapabilityProfile }
  }
  try {
    const parsed = JSON.parse(raw) as Partial<CapabilityProfile> | null
    if (!parsed) {
      return { ...defaultCapabilityProfile }
    }
    return {
      ...defaultCapabilityProfile,
      ...parsed,
      seo_level: 'deep'
    }
  } catch (error) {
    return { ...defaultCapabilityProfile }
  }
}

export function saveCapabilityProfile(
  profile: CapabilityProfile,
  storage: Pick<Storage, 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.setItem) {
    return
  }
  try {
    storage.setItem(CAPABILITY_STORAGE_KEY, JSON.stringify({ ...profile, seo_level: 'deep' }))
  } catch (error) {
    // ignore storage errors
  }
}

export function loadCapabilitySettingsSkip(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
): boolean {
  if (!storage?.getItem) {
    return false
  }
  try {
    return storage.getItem(CAPABILITY_SETTINGS_SKIP_STORAGE_KEY) === '1'
  } catch (error) {
    return false
  }
}

export function persistCapabilitySettingsSkip(
  value: boolean,
  storage: Pick<Storage, 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.setItem) {
    return
  }
  try {
    storage.setItem(CAPABILITY_SETTINGS_SKIP_STORAGE_KEY, value ? '1' : '0')
  } catch (error) {
    // ignore storage errors
  }
}

export async function fetchCapabilityPreferences(): Promise<CapabilityPreferences | null> {
  const token = resolveAuthToken()
  if (!token) {
    return null
  }
  const apiBaseUrl = resolveApiBaseUrl()
  try {
    const response = await fetch(`${apiBaseUrl}/api/v1/capabilities/preferences`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    })
    if (!response.ok) {
      return null
    }
    const payload = (await response.json().catch(() => null)) as any
    const prefs = payload?.preferences
    if (!prefs || typeof prefs !== 'object') {
      return null
    }
    return {
      capability_settings_skip: Boolean((prefs as any).capability_settings_skip),
    }
  } catch (error) {
    return null
  }
}

export async function updateCapabilityPreferences(
  updates: Partial<CapabilityPreferences>
): Promise<CapabilityPreferences | null> {
  const token = resolveAuthToken()
  if (!token) {
    return null
  }
  const apiBaseUrl = resolveApiBaseUrl()
  try {
    const response = await fetch(`${apiBaseUrl}/api/v1/capabilities/preferences`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updates),
    })
    if (!response.ok) {
      return null
    }
    const payload = (await response.json().catch(() => null)) as any
    const prefs = payload?.preferences
    if (!prefs || typeof prefs !== 'object') {
      return null
    }
    return {
      capability_settings_skip: Boolean((prefs as any).capability_settings_skip),
    }
  } catch (error) {
    return null
  }
}

export async function syncCapabilityPreferences(): Promise<CapabilityPreferences | null> {
  const prefs = await fetchCapabilityPreferences()
  if (!prefs) {
    return null
  }
  persistCapabilitySettingsSkip(prefs.capability_settings_skip)
  return prefs
}

export async function setCapabilitySettingsSkip(value: boolean): Promise<boolean> {
  persistCapabilitySettingsSkip(value)
  const prefs = await updateCapabilityPreferences({ capability_settings_skip: value })
  if (!prefs) {
    return value
  }
  persistCapabilitySettingsSkip(prefs.capability_settings_skip)
  return prefs.capability_settings_skip
}

export type AiConfigStyle = 'professional' | 'creative' | 'concise' | 'custom'
export type AiConfigSeoLevel = 'standard' | 'deep'
export type AiConfig = {
  platform: CapabilityProfile['platform']
  marketRegion: CapabilityProfile['market_region']
  contentStyle: AiConfigStyle
  seoLevel: AiConfigSeoLevel
  language: CapabilityProfile['language']
  enableImageGeneration: boolean
  imageCount: number
}

export function mapCapabilityProfileToAiConfig(profile: CapabilityProfile): AiConfig {
  const presets: Record<CapabilityStrategyTier, number> = {
    economy: 3,
    balanced: 2,
    premium: 8
  }
  const fallbackCount = presets[profile.strategy_tier] ?? presets.balanced
  const raw = (profile as { image_max_images?: unknown }).image_max_images
  const resolved =
    typeof raw === 'number' && Number.isFinite(raw) ? Math.round(raw) : typeof raw === 'string' ? parseInt(raw, 10) : fallbackCount
  const clamped = Math.max(1, Math.min(10, Number.isFinite(resolved) ? resolved : fallbackCount))
  return {
    platform: profile.platform,
    marketRegion: profile.market_region,
    contentStyle: profile.content_style,
    seoLevel: 'deep',
    language: profile.language,
    enableImageGeneration: true,
    imageCount: clamped
  }
}

export function mapAiConfigToCapabilityProfile(
  config: AiConfig,
  baseProfile: CapabilityProfile
): CapabilityProfile {
  const safeCount = Math.max(1, Math.min(10, Math.round(config.imageCount || 1)))
  return {
    ...baseProfile,
    platform: config.platform,
    market_region: config.marketRegion,
    content_style: config.contentStyle,
    language: config.language,
    image_max_images: safeCount,
    seo_level: 'deep'
  }
}

export const defaultAiConfig: AiConfig = mapCapabilityProfileToAiConfig(defaultCapabilityProfile)

const AI_CONFIG_STORAGE_KEY = 'ai_config'

export function loadAiConfig(
  storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
): AiConfig {
  return mapCapabilityProfileToAiConfig(loadCapabilityProfile(storage))
}

export function saveAiConfig(
  config: AiConfig,
  storage: Pick<Storage, 'getItem' | 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage
) {
  if (!storage?.setItem) {
    return
  }
  const baseProfile = loadCapabilityProfile(storage)
  const nextProfile = mapAiConfigToCapabilityProfile(config, baseProfile)
  saveCapabilityProfile(nextProfile, storage)
  try {
    storage.setItem(AI_CONFIG_STORAGE_KEY, JSON.stringify({ ...config, seoLevel: 'deep' }))
  } catch (error) {
    // ignore storage errors
  }
}

export function resolveApiBaseUrl(envBaseUrl?: string, originOverride?: string) {
  const cleanedEnv = envBaseUrl?.trim()
  if (cleanedEnv) {
    return cleanedEnv.replace(/\/$/, '')
  }

  const metaEnv = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL
  if (metaEnv) {
    return metaEnv.replace(/\/$/, '')
  }

  const origin = originOverride ?? (typeof window !== 'undefined' ? window.location.origin : '')
  if (origin) {
    const trimmedOrigin = origin.replace(/\/$/, '')
    try {
      const url = new URL(trimmedOrigin)
      const hostname = url.hostname
      const port = url.port
      const normalizedHost = hostname === '0.0.0.0' ? 'localhost' : hostname
      if (normalizedHost === 'yijianmai.com' || normalizedHost === 'www.yijianmai.com') {
        return `${url.protocol}//api.yijianmai.com`
      }
      if (normalizedHost === 'staging.yijianmai.com') {
        return `${url.protocol}//staging-api.yijianmai.com`
      }
      const isLocalHost = normalizedHost === 'localhost' || normalizedHost === '127.0.0.1' || normalizedHost === '::1'
      const isPrivateIp =
        normalizedHost.startsWith('10.') ||
        normalizedHost.startsWith('192.168.') ||
        /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
      if ((isLocalHost || isPrivateIp) && port && port !== '8000') {
        return `${url.protocol}//${normalizedHost}:8000`
      }
    } catch (error) {
      if (trimmedOrigin.includes(':5666')) {
        if (trimmedOrigin.includes('0.0.0.0')) {
          return trimmedOrigin.replace('0.0.0.0:5666', 'localhost:8000')
        }
        return trimmedOrigin.replace(':5666', ':8000')
      }
    }
    return trimmedOrigin
  }

  return 'http://localhost:8000'
}

export type MarketingEventPayload = {
  event_name: string
  event_source?: string
  page_url?: string
  page_path?: string
  referrer_url?: string
  visitor_id?: string
  session_id?: string
  language?: string
  occurred_at?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  properties?: Record<string, unknown>
}

type SendMarketingEventOptions = {
  apiBaseUrl?: string
  transport?: 'fetch' | 'beacon'
  keepalive?: boolean
}

const MARKETING_VISITOR_ID_STORAGE_KEY = 'marketing-visitor-id'
const MARKETING_SESSION_ID_STORAGE_KEY = 'marketing-session-id'

const generateMarketingId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `mk_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

const resolveStorage = <T extends Storage>(factory: () => T | null): T | null => {
  try {
    return factory()
  } catch (_error) {
    return null
  }
}

export const getMarketingVisitorId = () => {
  if (typeof window === 'undefined') {
    return ''
  }
  const storage = resolveStorage(() => window.localStorage)
  if (!storage) {
    return ''
  }
  const cached = storage.getItem(MARKETING_VISITOR_ID_STORAGE_KEY)
  if (cached) {
    return cached
  }
  const nextId = generateMarketingId()
  try {
    storage.setItem(MARKETING_VISITOR_ID_STORAGE_KEY, nextId)
  } catch (_error) {
    return ''
  }
  return nextId
}

export const getMarketingSessionId = () => {
  if (typeof window === 'undefined') {
    return ''
  }
  const storage = resolveStorage(() => window.sessionStorage)
  if (!storage) {
    return ''
  }
  const cached = storage.getItem(MARKETING_SESSION_ID_STORAGE_KEY)
  if (cached) {
    return cached
  }
  const nextId = generateMarketingId()
  try {
    storage.setItem(MARKETING_SESSION_ID_STORAGE_KEY, nextId)
  } catch (_error) {
    return ''
  }
  return nextId
}

export async function sendMarketingEvents(
  events: MarketingEventPayload[],
  options: SendMarketingEventOptions = {}
): Promise<boolean> {
  if (typeof window === 'undefined' || events.length === 0) {
    return false
  }
  const baseUrl = resolveApiBaseUrl(options.apiBaseUrl)
  const endpoint = `${baseUrl}/api/v1/public/analytics/events`

  const normalizedEvents = events
    .map((event) => {
      const pathWithQuery = `${window.location.pathname}${window.location.search}`
      return {
        event_name: event.event_name,
        event_source: event.event_source || 'webapp',
        page_url: event.page_url || window.location.href,
        page_path: event.page_path || pathWithQuery,
        referrer_url: event.referrer_url || document.referrer || undefined,
        visitor_id: event.visitor_id || getMarketingVisitorId() || undefined,
        session_id: event.session_id || getMarketingSessionId() || undefined,
        language: event.language || document.documentElement.lang || navigator.language || undefined,
        occurred_at: event.occurred_at || new Date().toISOString(),
        utm_source: event.utm_source,
        utm_medium: event.utm_medium,
        utm_campaign: event.utm_campaign,
        utm_term: event.utm_term,
        utm_content: event.utm_content,
        properties: event.properties || {}
      }
    })
    .filter((event) => Boolean(event.event_name))

  if (normalizedEvents.length === 0) {
    return false
  }

  const body = JSON.stringify({ events: normalizedEvents })

  if (options.transport === 'beacon' && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const beaconPayload = new Blob([body], { type: 'application/json' })
    return navigator.sendBeacon(endpoint, beaconPayload)
  }

  const token = resolveAuthToken()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body,
      keepalive: options.keepalive ?? false,
    })
    return response.ok
  } catch (_error) {
    return false
  }
}

export async function sendMarketingEvent(
  event: MarketingEventPayload,
  options: SendMarketingEventOptions = {}
): Promise<boolean> {
  return await sendMarketingEvents([event], options)
}

export type PublicAppConfigLinks = {
  tutorial_url: string
  user_guide_url: string
  help_center_url: string
  privacy_url: string
  terms_url: string
  security_whitepaper_url: string
  copyright_whitepaper_url: string
  legal_certificates_url: string
  task_center_batch_template_url: string
}

export type PublicSupportChannel = {
  key: string
  title: string
  subtitle: string
  value: string
  badge?: string | null
}

export type PublicSupportGroup = {
  title: string
  subtitle: string
}

export type PublicSupportConfig = {
  headline: string
  subtitle: string
  benefit_title: string
  benefit_desc: string
  channels: PublicSupportChannel[]
  group: PublicSupportGroup
}

export type PublicAppConfigPayload = {
  success?: boolean
  version?: string
  links?: PublicAppConfigLinks
  support?: PublicSupportConfig
  feature_flags?: Record<string, boolean | string | number | null>
}

let publicAppConfigCache: PublicAppConfigPayload | null = null
let publicAppConfigCacheAt = 0
let publicAppConfigInflight: Promise<PublicAppConfigPayload | null> | null = null

const PUBLIC_APP_CONFIG_TTL_MS = 5 * 60 * 1000

export async function fetchPublicAppConfig(input?: { apiBaseUrl?: string; force?: boolean }) {
  if (typeof fetch === 'undefined') {
    return null
  }
  const now = Date.now()
  const shouldUseCache =
    !input?.force &&
    publicAppConfigCache &&
    publicAppConfigCacheAt > 0 &&
    now - publicAppConfigCacheAt < PUBLIC_APP_CONFIG_TTL_MS
  if (shouldUseCache) {
    return publicAppConfigCache
  }

  if (publicAppConfigInflight) {
    return await publicAppConfigInflight
  }

  publicAppConfigInflight = (async () => {
    try {
      const baseUrl = resolveApiBaseUrl(input?.apiBaseUrl)
      const response = await fetch(`${baseUrl}/api/v1/public/app-config`)
      const payload = (await response.json().catch(() => null)) as PublicAppConfigPayload | null
      if (!response.ok || !payload) {
        return null
      }
      publicAppConfigCache = payload
      publicAppConfigCacheAt = Date.now()
      return payload
    } catch (error) {
      return null
    } finally {
      publicAppConfigInflight = null
    }
  })()

  return await publicAppConfigInflight
}

export function resolveAbsoluteUrl(urlOrPath: string, apiBaseUrl?: string) {
  if (!urlOrPath) {
    return ''
  }
  const trimmed = urlOrPath.trim()
  if (!trimmed) {
    return ''
  }
  let resolved = trimmed
  if (trimmed.startsWith('/')) {
    const baseUrl = resolveApiBaseUrl(apiBaseUrl)
    resolved = `${baseUrl}${trimmed}`
  }

  if (typeof window !== 'undefined' && (resolved.startsWith('http://') || resolved.startsWith('https://'))) {
    try {
      const parsed = new URL(resolved)
      const legalPaths = ['/api/v1/public/legal/privacy', '/api/v1/public/legal/terms']
      if (legalPaths.includes(parsed.pathname) && !parsed.searchParams.has('return_url')) {
        parsed.searchParams.set('return_url', window.location.href)
        resolved = parsed.toString()
      }
    } catch (error) {
      // ignore
    }
  }

  return resolved
}

export type ExtensionGuideSource = 'header' | 'workspace' | 'import'

export type ExtensionGuideOpenDetail = {
  source?: ExtensionGuideSource
  forceRestart?: boolean
}

export const EXTENSION_GUIDE_OPEN_EVENT = 'extension-guide:open'

export function openExtensionInstallGuide(detail?: ExtensionGuideOpenDetail) {
  if (typeof window === 'undefined') {
    return
  }
  window.dispatchEvent(new CustomEvent<ExtensionGuideOpenDetail>(EXTENSION_GUIDE_OPEN_EVENT, { detail }))
}

export function splitOptionValues(value: string) {
  if (!value) return []
  return Array.from(
    new Set(
      value
        .split(/[\s,，;/|、]+/)
        .map((item) => item.trim())
        .filter(Boolean)
    )
  )
}

function extractValuesFromSkuName(name: string) {
  const values: string[] = []
  const patterns = [/【([^】]+)】/g, /\[([^\]]+)\]/g, /\(([^)]+)\)/g]
  for (const pattern of patterns) {
    let match = pattern.exec(name)
    while (match) {
      values.push(...splitOptionValues(match[1]))
      match = pattern.exec(name)
    }
  }
  return values
}

function normalizeNumber(value: unknown) {
  if (typeof value === 'number' && !Number.isNaN(value)) return value
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.]+/g, '')
    const parsed = Number(cleaned)
    if (!Number.isNaN(parsed)) return parsed
  }
  return null
}

function formatDimensionValue(mmValue: number) {
  const cm = mmValue / 10
  const fixed = Number.isInteger(cm) ? cm.toString() : cm.toFixed(1)
  return fixed
}

function formatWeight(grams: number) {
  const kg = grams / 1000
  const fixed = kg.toFixed(2).replace(/\.?0+$/, '')
  return `${fixed} kg`
}

function humanizeKey(key: string) {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : ''))
    .join(' ')
}

function stripUnitSuffix(key: string) {
  const cleaned = key.replace(/_(mm|cm|m|g|kg)$/i, '').replace(/(mm|cm|m|g|kg)$/i, '')
  return cleaned || key
}

export type SkuInsights = {
  colors: string[]
  sizes: string[]
  price: number | null
  stock: number | null
  weightText: string | null
  dimensionsText: string | null
}

export function extractSkuInsights(extractedData?: Record<string, any>): SkuInsights {
  const colors = new Set<string>()
  const sizes = new Set<string>()
  let price: number | null = null
  let stock: number | null = null
  let weightText: string | null = null
  let dimensionsText: string | null = null

  if (!extractedData) {
    return { colors: [], sizes: [], price, stock, weightText, dimensionsText }
  }

  const selectPrice = (value: unknown) => {
    const parsed = normalizeNumber(value)
    if (parsed !== null) {
      price = parsed
    }
  }

  if (extractedData.selected_sku?.price !== undefined) {
    selectPrice(extractedData.selected_sku.price)
  }

  if (price === null && extractedData.price !== undefined) {
    selectPrice(extractedData.price)
  }

  if (price === null && typeof extractedData.price_range === 'string') {
    selectPrice(extractedData.price_range)
  }

  if (extractedData.selected_sku?.stock !== undefined) {
    const parsed = normalizeNumber(extractedData.selected_sku.stock)
    if (parsed !== null) {
      stock = parsed
    }
  }

  const attributeEntries = extractedData.parsed_attributes
    ? Object.entries(extractedData.parsed_attributes)
    : []

  attributeEntries.forEach(([key, value]) => {
    const val = typeof value === 'string' ? value : ''
    if (!val) return
    if (/颜色|color|colour/i.test(key)) {
      splitOptionValues(val).forEach((item) => colors.add(item))
    }
    if (/尺码|尺寸|size/i.test(key)) {
      splitOptionValues(val).forEach((item) => sizes.add(item))
    }
  })

  if (Array.isArray(extractedData.parsed_skus)) {
    extractedData.parsed_skus.forEach((sku: Record<string, any>) => {
      if (price === null && sku?.price !== undefined) {
        selectPrice(sku.price)
      }

      const skuStock = normalizeNumber(sku?.stock)
      if (skuStock !== null) {
        stock = stock === null ? skuStock : Math.max(stock, skuStock)
      }

      const attributes = sku?.attributes && typeof sku.attributes === 'object' ? sku.attributes : null
      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          const val = typeof value === 'string' ? value : ''
          if (!val) return
          if (/颜色|color|colour/i.test(key)) {
            splitOptionValues(val).forEach((item) => colors.add(item))
          }
          if (/尺码|尺寸|size/i.test(key)) {
            splitOptionValues(val).forEach((item) => sizes.add(item))
          }
        })
      }

      const variantType = sku?.variant_type || sku?.variantType || sku?.type
      const variantValue = sku?.variant_value || sku?.variantValue || sku?.value
      if (typeof variantType === 'string' && typeof variantValue === 'string') {
        if (/颜色|color|colour/i.test(variantType)) {
          splitOptionValues(variantValue).forEach((item) => colors.add(item))
        }
        if (/尺码|尺寸|size/i.test(variantType)) {
          splitOptionValues(variantValue).forEach((item) => sizes.add(item))
        }
      } else if (typeof sku?.name === 'string') {
        const values = extractValuesFromSkuName(sku.name)
        values.forEach((item) => {
          if (!colors.has(item) && sizes.size === 0) {
            colors.add(item)
          } else {
            sizes.add(item)
          }
        })
      }
    })
  }

  const dimensions = extractedData.parsed_dimensions
  if (dimensions && typeof dimensions === 'object') {
    const lengthMm = normalizeNumber(dimensions.length_mm)
    const widthMm = normalizeNumber(dimensions.width_mm)
    const heightMm = normalizeNumber(dimensions.height_mm)
    const weightG = normalizeNumber(dimensions.weight_g)

    if (lengthMm !== null && widthMm !== null && heightMm !== null) {
      dimensionsText = `${formatDimensionValue(lengthMm)} x ${formatDimensionValue(widthMm)} x ${formatDimensionValue(heightMm)} cm`
    }
    if (weightG !== null) {
      weightText = formatWeight(weightG)
    }
  }

  return {
    colors: Array.from(colors),
    sizes: Array.from(sizes),
    price,
    stock,
    weightText,
    dimensionsText
  }
}

export function resolvePreviewImageUrl(image: unknown): string {
  if (typeof image === 'string') {
    return image.trim()
  }
  if (!image || typeof image !== 'object') {
    return ''
  }
  const record = image as Record<string, unknown>
  const candidates = [
    record.storage_path,
    record.original_url,
    record.originalUrl,
    record.url,
    record.image_url,
    record.imageUrl,
    record.src
  ]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim()
    }
  }
  return ''
}

function normalizePreviewImageKey(image: unknown): string {
  const url = resolvePreviewImageUrl(image)
  if (!url) {
    return ''
  }
  return url
    .split('?')[0]
    .split('#')[0]
    .replace(/^https?:\/\//i, '')
    .toLowerCase()
}

export function buildPreviewImages(preview: { images?: Array<{ storage_path?: string; original_url?: string; url?: string }>; extracted_data?: Record<string, any> }) {
  const images = Array.isArray(preview?.images)
    ? preview.images
      .map((img) => resolvePreviewImageUrl(img))
      .filter((item) => item.length > 0)
    : []

  if (images.length > 0) {
    return images
  }

  const extractedImages = preview?.extracted_data?.images
  if (Array.isArray(extractedImages) && extractedImages.length > 0) {
    return extractedImages
      .map((img) => resolvePreviewImageUrl(img))
      .filter((item) => item.length > 0)
  }

  const skus = Array.isArray(preview?.extracted_data?.parsed_skus) ? preview.extracted_data.parsed_skus : []
  const skuImages = skus
    .map((sku: Record<string, any>) => sku?.image_url || sku?.imageUrl || sku?.image)
    .filter(Boolean)

  if (skuImages.length > 0) {
    return skuImages
  }

  return []
}

export function buildSkuAnchorCandidates(preview: {
  images?: Array<{ storage_path?: string; original_url?: string; url?: string; source_category?: string } | string>
  extracted_data?: Record<string, any> | null
}) {
  const extractedImages = Array.isArray(preview?.extracted_data?.images)
    ? preview.extracted_data.images
      .map((img, index) => ({ index, url: resolvePreviewImageUrl(img) }))
      .filter((item) => item.url.length > 0)
    : []

  if (extractedImages.length === 0) {
    return []
  }

  const skuImageKeys = new Set<string>()

  if (Array.isArray(preview?.images)) {
    preview.images.forEach((img) => {
      if (!img || typeof img !== 'object' || Array.isArray(img)) {
        return
      }
      const record = img as Record<string, unknown>
      const category = String(record.source_category ?? '').trim().toLowerCase()
      if (category !== 'sku') {
        return
      }
      const key = normalizePreviewImageKey(record)
      if (key) {
        skuImageKeys.add(key)
      }
    })
  }

  if (Array.isArray(preview?.extracted_data?.parsed_skus)) {
    preview.extracted_data.parsed_skus.forEach((sku) => {
      if (!sku || typeof sku !== 'object') {
        return
      }
      const record = sku as Record<string, unknown>
      const key = normalizePreviewImageKey(
        record.image_url || record.imageUrl || record.image || record.url || record.src
      )
      if (key) {
        skuImageKeys.add(key)
      }
    })
  }

  if (skuImageKeys.size > 0) {
    const filteredImages = extractedImages.filter((item) => skuImageKeys.has(normalizePreviewImageKey(item.url)))
    if (filteredImages.length > 0) {
      return filteredImages
    }
  }

  return extractedImages
}

export function derivePricingInsights(extractedData?: Record<string, any>, fallbackPrice?: number | null) {
  const prices: number[] = []
  const pushPrice = (value: unknown) => {
    const parsed = normalizeNumber(value)
    if (parsed !== null) {
      prices.push(parsed)
    }
  }

  if (extractedData?.price_range) {
    const rangeValues = String(extractedData.price_range).match(/\d+(?:\.\d+)?/g) || []
    rangeValues.map(Number).forEach((value) => {
      if (!Number.isNaN(value)) {
        prices.push(value)
      }
    })
  }

  if (Array.isArray(extractedData?.parsed_skus)) {
    extractedData.parsed_skus.forEach((sku: Record<string, any>) => {
      pushPrice(sku?.price)
    })
  }

  if (prices.length === 0 && fallbackPrice !== null && fallbackPrice !== undefined) {
    prices.push(fallbackPrice)
  }

  if (prices.length === 0) {
    return { marketAvg: null, suggested: null }
  }

  const min = Math.min(...prices)
  const max = Math.max(...prices)
  const marketAvg = Number(((min + max) / 2).toFixed(2))
  const suggested = Number((marketAvg * 1.1).toFixed(2))

  return { marketAvg, suggested }
}

export function buildAttributeList(extractedData?: Record<string, any>) {
  const attributes = extractedData?.parsed_attributes
  if (!attributes || typeof attributes !== 'object') {
    return []
  }

  const entries = Array.isArray(attributes)
    ? attributes
      .filter((item) => item && typeof item === 'object')
      .map((item) => [item.key ?? item.label ?? item.name ?? '', item.value ?? ''] as [string, string])
    : Object.entries(attributes)

  return entries
    .filter(([key, value]) => typeof value === 'string' && value.trim())
    .filter(([key]) => !/颜色|color|colour|尺码|尺寸|size/i.test(String(key)))
    .map(([key, value]) => ({ label: String(key), value: String(value) }))
}

export function buildHighlightList(extractedData?: Record<string, any>) {
  const criticalInfo = Array.isArray(extractedData?.critical_info) ? extractedData.critical_info : []
  const mapped = criticalInfo
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      title: String(item.type ?? item.title ?? '').trim(),
      detail: String(item.content ?? item.detail ?? '').trim()
    }))
    .filter((item) => item.title && item.detail)

  if (mapped.length > 0) {
    return mapped
  }

  const attributes = extractedData?.parsed_attributes
  if (!attributes || typeof attributes !== 'object') {
    return []
  }

  const entries = Array.isArray(attributes)
    ? attributes
      .filter((item) => item && typeof item === 'object')
      .map((item) => [item.key ?? item.label ?? item.name ?? '', item.value ?? ''] as [string, string])
    : Object.entries(attributes)

  return entries
    .filter(([key, value]) => typeof value === 'string' && value.trim())
    .filter(([key]) => !/颜色|color|colour|尺码|尺寸|size/i.test(String(key)))
    .map(([key, value]) => ({ title: String(key), detail: String(value) }))
}

export function buildFlagList(extractedData?: Record<string, any>) {
  const flags = extractedData?.flags
  if (!flags || typeof flags !== 'object') {
    return []
  }

  const entries = Array.isArray(flags)
    ? flags
      .filter((item) => item && typeof item === 'object')
      .map((item) => [item.key ?? item.label ?? item.name ?? '', item.value] as [string, unknown])
    : Object.entries(flags)

  return entries
    .filter(([key, value]) => Boolean(key) && value !== undefined && value !== null)
    .map(([key, value]) => {
      let normalizedValue: boolean | string | number
      if (typeof value === 'boolean') {
        normalizedValue = value
      } else if (typeof value === 'number') {
        normalizedValue = value
      } else if (typeof value === 'string') {
        const trimmed = value.trim()
        if (trimmed.toLowerCase() === 'true') {
          normalizedValue = true
        } else if (trimmed.toLowerCase() === 'false') {
          normalizedValue = false
        } else {
          normalizedValue = trimmed
        }
      } else {
        normalizedValue = String(value)
      }

      return {
        label: humanizeKey(String(key)),
        value: normalizedValue
      }
    })
}

export function buildDimensionList(extractedData?: Record<string, any>) {
  const dimensions = extractedData?.parsed_dimensions
  if (!dimensions || typeof dimensions !== 'object') {
    return []
  }

  const entries = Array.isArray(dimensions)
    ? dimensions
      .filter((item) => item && typeof item === 'object')
      .map((item) => [item.key ?? item.label ?? item.name ?? '', item.value] as [string, unknown])
    : Object.entries(dimensions)

  return entries
    .filter(([key, value]) => Boolean(key) && value !== undefined && value !== null && value !== '')
    .map(([key, value]) => {
      const keyText = String(key)
      const numeric = normalizeNumber(value)
      const lowerKey = keyText.toLowerCase()
      let formatted = ''

      if (numeric !== null) {
        if (lowerKey.includes('weight') || keyText.includes('重量')) {
          if (lowerKey.includes('g')) {
            formatted = formatWeight(numeric)
          } else if (lowerKey.includes('kg')) {
            formatted = `${numeric} kg`
          } else {
            formatted = `${numeric}`
          }
        } else if (lowerKey.includes('mm')) {
          formatted = `${formatDimensionValue(numeric)} cm`
        } else if (lowerKey.includes('cm') || keyText.includes('厘米')) {
          formatted = `${numeric} cm`
        } else {
          formatted = `${numeric}`
        }
      } else {
        formatted = String(value)
      }

      return {
        label: humanizeKey(stripUnitSuffix(keyText)),
        value: formatted
      }
    })
}

export function buildImageTagList(extractedData?: Record<string, any>) {
  const tags = extractedData?.image_tags
  if (tags && typeof tags === 'object') {
    return Object.entries(tags)
      .map(([key, value]) => ({ key, count: Array.isArray(value) ? value.length : 0 }))
      .filter((item) => item.count > 0)
      .map((item) => ({ label: humanizeKey(item.key), count: item.count }))
  }

  const analysis = Array.isArray(extractedData?.image_analysis) ? extractedData?.image_analysis : []
  if (!analysis) {
    return []
  }

  const counts = new Map<string, number>()
  analysis.forEach((item: Record<string, any>) => {
    const tag = item?.tag
    if (typeof tag === 'string' && tag.trim()) {
      counts.set(tag, (counts.get(tag) || 0) + 1)
    }
  })

  return Array.from(counts.entries()).map(([key, count]) => ({
    label: humanizeKey(key),
    count
  }))
}

export function buildOverviewMetrics(preview: { images?: Array<{ storage_path?: string; original_url?: string; url?: string }>; extracted_data?: Record<string, any> }) {
  const images = Array.isArray(preview?.images) ? preview.images : []
  const extractedData = preview?.extracted_data

  const fallbackImages = Array.isArray(extractedData?.images) ? extractedData.images : []
  const imageCount = images.length > 0 ? images.length : fallbackImages.length

  const attributes = extractedData?.parsed_attributes
  const attributeCount = Array.isArray(attributes)
    ? attributes.length
    : attributes && typeof attributes === 'object'
      ? Object.keys(attributes).length
      : 0

  const skus = Array.isArray(extractedData?.parsed_skus) ? extractedData.parsed_skus : []
  const skusCount = skus.length

  const rawSections = extractedData?.raw_sections
  const sectionsCount =
    rawSections && typeof rawSections === 'object'
      ? Object.values(rawSections).filter((value) => typeof value === 'string' && value.trim()).length
      : 0

  const rawTextLength = typeof extractedData?.raw_page_text === 'string' ? extractedData.raw_page_text.length : 0

  return [
    { key: 'images', value: imageCount },
    { key: 'skus', value: skusCount },
    { key: 'attributes', value: attributeCount },
    { key: 'sections', value: sectionsCount },
    { key: 'rawText', value: rawTextLength }
  ]
}

export function buildCollectStepList(status?: string) {
  const normalized = status || 'pending'
  const steps = [
    { key: 'queued', status: 'pending' },
    { key: 'assigned', status: 'pending' },
    { key: 'collecting', status: 'pending' },
    { key: 'uploading', status: 'pending' },
    { key: 'completed', status: 'pending' }
  ] as Array<{ key: string; status: 'pending' | 'active' | 'done' | 'error' }>

  const markDoneUntil = (index: number) => {
    steps.forEach((step, idx) => {
      if (idx < index) {
        step.status = 'done'
      }
    })
    if (steps[index]) {
      steps[index].status = 'active'
    }
  }

  switch (normalized) {
    case 'pending':
    case 'queued':
      steps[0].status = 'active'
      break
    case 'assigned':
      markDoneUntil(1)
      break
    case 'collecting':
      markDoneUntil(2)
      break
    case 'uploading':
      markDoneUntil(3)
      break
    case 'completed':
      steps.forEach((step) => {
        step.status = 'done'
      })
      break
    case 'failed':
    case 'cancelled':
    case 'expired':
      steps.forEach((step, idx) => {
        if (idx < steps.length - 1) {
          step.status = 'done'
        }
      })
      steps[steps.length - 1].status = 'error'
      break
    default:
      steps[0].status = 'active'
      break
  }

  return steps
}

export function resolveCollectProgress(status?: string, progress?: number | null) {
  if (typeof progress === 'number' && !Number.isNaN(progress)) {
    return Math.max(0, Math.min(100, Math.round(progress)))
  }

  switch (status) {
    case 'assigned':
      return 25
    case 'collecting':
      return 55
    case 'uploading':
      return 80
    case 'completed':
      return 100
    case 'failed':
    case 'cancelled':
    case 'expired':
      return 100
    case 'pending':
    case 'queued':
    default:
      return 10
  }
}

export function resolveCollectOptions(usePolling: boolean) {
  if (usePolling) {
    return { polling: true, raw_mode: true }
  }
  return { raw_mode: true }
}

export function shouldFallbackToPolling(errorPayload?: { detail?: { code?: string }; code?: string }) {
  const code = errorPayload?.detail?.code || errorPayload?.code || ''
  return ['PLUGIN_NOT_CONNECTED', 'NO_AVAILABLE_DEVICE'].includes(code)
}

export function buildSkuStructure(extractedData?: Record<string, any>) {
  if (!Array.isArray(extractedData?.parsed_skus)) {
    return []
  }

  return extractedData.parsed_skus
    .filter((sku: Record<string, any>) => sku && (sku.name || sku.variant_value || sku.variantValue || sku.value))
    .map((sku: Record<string, any>, index: number) => {
      const attributes =
        sku.attributes && typeof sku.attributes === 'object'
          ? Object.entries(sku.attributes)
            .filter(([_, value]) => typeof value === 'string' && value.trim())
            .map(([key, value]) => ({ label: String(key), value: String(value) }))
          : []

      return {
        name: String(sku.name || sku.variant_value || sku.variantValue || sku.value || `SKU ${index + 1}`),
        price: normalizeNumber(sku.price),
        stock: normalizeNumber(sku.stock),
        variantType: String(sku.variant_type || sku.variantType || sku.type || ''),
        variantValue: String(sku.variant_value || sku.variantValue || sku.value || ''),
        attributes
      }
    })
}

export type SkuMatrixVariant = {
  key: string
  name: string
  price: number | null
  stock: number | null
  imageUrl: string
  attributes: Record<string, string>
}

export type SkuMatrixOption = {
  value: string
  label: string
  imageUrl?: string
}

export type SkuMatrixModel = {
  variants: SkuMatrixVariant[]
  attributeOrder: string[]
  optionsByAttribute: Record<string, SkuMatrixOption[]>
  source: 'parsed' | 'raw' | 'mixed' | 'single' | 'empty'
}

export type SkuMatrixState = {
  selected: Record<string, string>
  availability: Record<string, Record<string, boolean>>
  resolvedVariant: SkuMatrixVariant | null
  matchingVariants: SkuMatrixVariant[]
  priceMin: number | null
  priceMax: number | null
}

const HTML_ENTITY_REPLACEMENTS: Record<string, string> = {
  '&lt;': '<',
  '&gt;': '>',
  '&amp;': '&',
  '&quot;': '"',
  '&#39;': "'",
}

function decodeHtmlEntities(value: string) {
  if (!value.includes('&')) {
    return value
  }
  let decoded = value
  decoded = decoded.replace(/&(lt|gt|amp|quot);/gi, (match) => HTML_ENTITY_REPLACEMENTS[match.toLowerCase()] || match)
  decoded = decoded.replace(/&#39;/gi, "'")
  decoded = decoded.replace(/&#(\d+);/g, (_, digits: string) => {
    const codePoint = Number.parseInt(digits, 10)
    return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : `&#${digits};`
  })
  decoded = decoded.replace(/&#x([0-9a-f]+);/gi, (_, digits: string) => {
    const codePoint = Number.parseInt(digits, 16)
    return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : `&#x${digits};`
  })
  return decoded
}

function normalizeTextValue(value: unknown) {
  if (value === null || value === undefined) {
    return ''
  }
  return decodeHtmlEntities(String(value)).trim()
}

function normalizeSkuImageUrl(value: unknown) {
  const text = normalizeTextValue(value)
  if (!text) {
    return ''
  }
  if (text.startsWith('//')) {
    return `https:${text}`
  }
  return text
}

function isSkuVariantAvailable(variant: SkuMatrixVariant) {
  if (variant.stock === null || variant.stock === undefined) {
    return true
  }
  return variant.stock > 0
}

function normalizeSelectionKey(value: string) {
  return value.replace(/^;+|;+$/g, '').trim()
}

function safeParseSkuRaw(raw: unknown): Record<string, any> | null {
  if (!raw) {
    return null
  }
  if (typeof raw === 'object' && raw !== null) {
    return raw as Record<string, any>
  }
  if (typeof raw !== 'string') {
    return null
  }
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, any>
    }
  } catch (error) {
    return null
  }
  return null
}

function buildParsedSkuVariants(extractedData?: Record<string, any>) {
  if (!Array.isArray(extractedData?.parsed_skus)) {
    return []
  }

  return extractedData.parsed_skus
    .map((sku: Record<string, any>, index: number): SkuMatrixVariant | null => {
      if (!sku || typeof sku !== 'object') {
        return null
      }

      const key =
        normalizeTextValue(sku.sku_id || sku.skuId || sku.id)
        || `parsed_${index + 1}`
      const name =
        normalizeTextValue(sku.name || sku.variant_value || sku.variantValue || sku.value)
        || `SKU ${index + 1}`

      const attributes: Record<string, string> = {}
      if (sku.attributes && typeof sku.attributes === 'object') {
        Object.entries(sku.attributes).forEach(([attrKey, attrValue]) => {
          const keyText = normalizeTextValue(attrKey)
          const valueText = normalizeTextValue(attrValue)
          if (!keyText || !valueText) {
            return
          }
          attributes[keyText] = valueText
        })
      }

      const variantType = normalizeTextValue(sku.variant_type || sku.variantType || sku.type)
      const variantValue = normalizeTextValue(sku.variant_value || sku.variantValue || sku.value)
      if (!Object.keys(attributes).length && variantType && variantValue) {
        attributes[variantType] = variantValue
      }

      return {
        key,
        name,
        price: normalizeNumber(sku.price),
        stock: normalizeNumber(sku.stock),
        imageUrl: normalizeSkuImageUrl(sku.image_url || sku.imageUrl || sku.image),
        attributes
      }
    })
    .filter((item): item is SkuMatrixVariant => Boolean(item))
}

function buildRawSkuVariants(extractedData?: Record<string, any>) {
  const rawSections = extractedData?.raw_sections && typeof extractedData.raw_sections === 'object'
    ? extractedData.raw_sections
    : null
  const parsedRaw = safeParseSkuRaw(rawSections?.sku_raw_text ?? extractedData?.sku_raw_text ?? extractedData?.skuRawText)
  if (!parsedRaw) {
    return []
  }

  const skuModel = parsedRaw?.skuModel && typeof parsedRaw.skuModel === 'object' ? parsedRaw.skuModel : parsedRaw
  if (!skuModel || typeof skuModel !== 'object') {
    return []
  }

  const pathMeta = new Map<string, { attrKey: string; attrValue: string; imageUrl: string }>()
  const rawProps = Array.isArray(skuModel.skuProps) ? skuModel.skuProps : []

  rawProps.forEach((prop: Record<string, any>) => {
    if (!prop || typeof prop !== 'object') {
      return
    }
    const attrKey = normalizeTextValue(prop.prop || prop.propName || prop.name || prop.title)
    if (!attrKey) {
      return
    }
    const values = Array.isArray(prop.value) ? prop.value : Array.isArray(prop.values) ? prop.values : []
    values.forEach((valueItem: Record<string, any>) => {
      if (!valueItem || typeof valueItem !== 'object') {
        return
      }
      const attrValue = normalizeTextValue(valueItem.name || valueItem.value || valueItem.title)
      if (!attrValue) {
        return
      }
      const imageUrl = normalizeSkuImageUrl(
        valueItem.imageUrl || valueItem.image || valueItem.imgUrl || valueItem.imageURI || valueItem.imageUri
      )
      const candidates: string[] = []
      const propPath = normalizeTextValue(valueItem.propPath || valueItem.path || valueItem.specPath)
      if (propPath) {
        candidates.push(normalizeSelectionKey(propPath))
      }
      const propId = normalizeTextValue(prop.propId || prop.id)
      const valueId = normalizeTextValue(valueItem.valueId || valueItem.id)
      if (propId && valueId) {
        candidates.push(normalizeSelectionKey(`${propId}:${valueId}`))
      }
      candidates.forEach((candidate) => {
        if (!candidate) {
          return
        }
        pathMeta.set(candidate, { attrKey, attrValue, imageUrl })
      })
    })
  })

  const rawMap =
    (skuModel.skuMap && typeof skuModel.skuMap === 'object' ? skuModel.skuMap : null)
    || (skuModel.skuInfoMap && typeof skuModel.skuInfoMap === 'object' ? skuModel.skuInfoMap : null)
    || (skuModel.sku_map && typeof skuModel.sku_map === 'object' ? skuModel.sku_map : null)
    || (skuModel.sku_info_map && typeof skuModel.sku_info_map === 'object' ? skuModel.sku_info_map : null)

  if (!rawMap || typeof rawMap !== 'object') {
    return []
  }

  const variants: SkuMatrixVariant[] = []
  Object.entries(rawMap).forEach(([skuKey, skuInfo], index) => {
    if (!skuInfo || typeof skuInfo !== 'object') {
      return
    }
    const info = skuInfo as Record<string, any>

    const attributes: Record<string, string> = {}
    let imageUrl = normalizeSkuImageUrl(
      info.imageUrl || info.image || info.imgUrl || info.imageURI || info.imageUri
    )

    const tokens = String(skuKey).match(/\d+:\d+/g) || []
    tokens.forEach((token) => {
      const normalizedToken = normalizeSelectionKey(token)
      const meta = pathMeta.get(normalizedToken)
      if (!meta) {
        return
      }
      attributes[meta.attrKey] = meta.attrValue
      if (!imageUrl && meta.imageUrl) {
        imageUrl = meta.imageUrl
      }
    })

    const fallbackName = Object.values(attributes).join(' / ')
    const name = normalizeTextValue(info.skuName || info.name || info.title || fallbackName || skuKey) || `SKU ${index + 1}`
    const key = normalizeTextValue(info.skuId || info.sku_id || info.id || skuKey) || `raw_${index + 1}`

    variants.push({
      key,
      name,
      price: normalizeNumber(info.price || info.priceText || info.discountPrice || info.discount_price),
      stock: normalizeNumber(info.canBookCount || info.stock || info.quantity),
      imageUrl,
      attributes
    })
  })

  return variants
}

function hasAttributeData(variants: SkuMatrixVariant[]) {
  return variants.some((variant) => Object.keys(variant.attributes).length > 0)
}

function mergeParsedAndRawVariants(parsedVariants: SkuMatrixVariant[], rawVariants: SkuMatrixVariant[]) {
  if (!parsedVariants.length) {
    return { variants: rawVariants, source: rawVariants.length ? ('raw' as const) : ('empty' as const) }
  }
  if (!rawVariants.length) {
    return { variants: parsedVariants, source: hasAttributeData(parsedVariants) ? ('parsed' as const) : ('single' as const) }
  }

  const parsedHasAttr = hasAttributeData(parsedVariants)
  const rawHasAttr = hasAttributeData(rawVariants)
  const merged = parsedVariants.map((variant, index) => {
    const rawMatch =
      rawVariants.find((rawVariant) => rawVariant.key === variant.key)
      || (parsedVariants.length === rawVariants.length ? rawVariants[index] : null)

    if (!rawMatch) {
      return variant
    }

    const attributes = Object.keys(variant.attributes).length ? variant.attributes : rawMatch.attributes
    return {
      ...variant,
      attributes,
      imageUrl: variant.imageUrl || rawMatch.imageUrl || '',
      price: variant.price ?? rawMatch.price,
      stock: variant.stock ?? rawMatch.stock
    }
  })

  if (!parsedHasAttr && rawHasAttr) {
    const alignedRaw = rawVariants.map((variant, index) => {
      const parsedMatch = parsedVariants[index]
      if (!parsedMatch) {
        return variant
      }
      return {
        ...variant,
        price: variant.price ?? parsedMatch.price,
        stock: variant.stock ?? parsedMatch.stock,
        imageUrl: variant.imageUrl || parsedMatch.imageUrl || ''
      }
    })
    return { variants: alignedRaw, source: 'mixed' as const }
  }

  return { variants: merged, source: parsedHasAttr ? ('parsed' as const) : ('mixed' as const) }
}

function ensurePseudoSkuAttribute(variants: SkuMatrixVariant[]) {
  if (variants.length <= 1 || hasAttributeData(variants)) {
    return variants
  }
  return variants.map((variant) => ({
    ...variant,
    attributes: { SKU: variant.name || variant.key }
  }))
}

function buildSkuOptions(variants: SkuMatrixVariant[]) {
  const attributeOrder: string[] = []
  const optionMap: Record<string, Map<string, SkuMatrixOption>> = {}

  variants.forEach((variant) => {
    Object.entries(variant.attributes).forEach(([attrKeyRaw, attrValueRaw]) => {
      const attrKey = normalizeTextValue(attrKeyRaw)
      const attrValue = normalizeTextValue(attrValueRaw)
      if (!attrKey || !attrValue) {
        return
      }
      if (!attributeOrder.includes(attrKey)) {
        attributeOrder.push(attrKey)
      }
      if (!optionMap[attrKey]) {
        optionMap[attrKey] = new Map<string, SkuMatrixOption>()
      }
      if (!optionMap[attrKey].has(attrValue)) {
        optionMap[attrKey].set(attrValue, {
          value: attrValue,
          label: attrValue,
          imageUrl: variant.imageUrl || undefined
        })
      }
    })
  })

  const optionsByAttribute: Record<string, SkuMatrixOption[]> = {}
  Object.entries(optionMap).forEach(([attrKey, valueMap]) => {
    optionsByAttribute[attrKey] = Array.from(valueMap.values())
  })

  return { attributeOrder, optionsByAttribute }
}

function variantMatchesSelection(variant: SkuMatrixVariant, selection: Record<string, string>) {
  return Object.entries(selection).every(([attrKey, attrValue]) => {
    const expected = normalizeTextValue(attrValue)
    if (!expected) {
      return true
    }
    return normalizeTextValue(variant.attributes[attrKey]) === expected
  })
}

function hasMatchingVariant(variants: SkuMatrixVariant[], selection: Record<string, string>) {
  return variants.some((variant) => variantMatchesSelection(variant, selection))
}

export function buildSkuMatrixModel(extractedData?: Record<string, any>): SkuMatrixModel {
  const parsedVariants = buildParsedSkuVariants(extractedData)
  const rawVariants = buildRawSkuVariants(extractedData)
  const mergedResult = mergeParsedAndRawVariants(parsedVariants, rawVariants)
  const variants = ensurePseudoSkuAttribute(mergedResult.variants)
  if (!variants.length) {
    return {
      variants: [],
      attributeOrder: [],
      optionsByAttribute: {},
      source: 'empty'
    }
  }

  const { attributeOrder, optionsByAttribute } = buildSkuOptions(variants)
  const source = attributeOrder.length > 0 ? mergedResult.source : 'single'

  return {
    variants,
    attributeOrder,
    optionsByAttribute,
    source
  }
}

export function computeSkuMatrixState(model: SkuMatrixModel, currentSelection: Record<string, string> = {}): SkuMatrixState {
  if (!model.variants.length) {
    return {
      selected: {},
      availability: {},
      resolvedVariant: null,
      matchingVariants: [],
      priceMin: null,
      priceMax: null
    }
  }

  const selected: Record<string, string> = {}
  const parsedSelection = currentSelection && typeof currentSelection === 'object' ? currentSelection : {}
  const hasStockVariants = model.variants.some((variant) => isSkuVariantAvailable(variant))
  const scopeVariants = hasStockVariants ? model.variants.filter((variant) => isSkuVariantAvailable(variant)) : model.variants

  model.attributeOrder.forEach((attrKey) => {
    const options = model.optionsByAttribute[attrKey] || []
    const baseSelection = { ...selected }
    const availableValuesInStock = options
      .filter((option) => hasMatchingVariant(scopeVariants, { ...baseSelection, [attrKey]: option.value }))
      .map((option) => option.value)
    const availableValuesAll = options
      .filter((option) => hasMatchingVariant(model.variants, { ...baseSelection, [attrKey]: option.value }))
      .map((option) => option.value)
    const preferredValue = normalizeTextValue(parsedSelection[attrKey])
    if (preferredValue && availableValuesAll.includes(preferredValue)) {
      selected[attrKey] = preferredValue
      return
    }
    if (availableValuesInStock.length > 0) {
      selected[attrKey] = availableValuesInStock[0]
      return
    }
    if (availableValuesAll.length > 0) {
      selected[attrKey] = availableValuesAll[0]
      return
    }
    if (options[0]?.value) {
      selected[attrKey] = options[0].value
    }
  })

  const availability: Record<string, Record<string, boolean>> = {}
  model.attributeOrder.forEach((attrKey) => {
    const options = model.optionsByAttribute[attrKey] || []
    availability[attrKey] = {}
    options.forEach((option) => {
      const probeSelection = {
        ...selected,
        [attrKey]: option.value
      }
      const matchAny = hasMatchingVariant(model.variants, probeSelection)
      availability[attrKey][option.value] = matchAny
    })
  })

  const matchingVariants = model.variants.filter((variant) => variantMatchesSelection(variant, selected))
  const preferredResolved = matchingVariants.find((variant) => isSkuVariantAvailable(variant))
  const resolvedVariant = preferredResolved || matchingVariants[0] || null

  const pricedVariants = matchingVariants.length > 0 ? matchingVariants : model.variants
  const prices = pricedVariants
    .map((variant) => variant.price)
    .filter((price): price is number => typeof price === 'number' && Number.isFinite(price))
  const priceMin = prices.length ? Math.min(...prices) : null
  const priceMax = prices.length ? Math.max(...prices) : null

  return {
    selected,
    availability,
    resolvedVariant,
    matchingVariants,
    priceMin,
    priceMax
  }
}

export function buildRawSectionList(extractedData?: Record<string, any>) {
  const sections = extractedData?.raw_sections
  if (!sections || typeof sections !== 'object') {
    return []
  }

  return Object.entries(sections)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => {
      if (typeof value === 'string') {
        return { label: key, content: value }
      }
      return { label: key, content: JSON.stringify(value, null, 2) }
    })
    .filter((item) => item.content && item.content.trim())
}

const normalizeScheduleStepMinutes = (stepMinutes: number) => {
  if (!Number.isFinite(stepMinutes) || stepMinutes <= 0) {
    return 5
  }
  return Math.max(1, Math.round(stepMinutes))
}

const resolveValidDate = (value: Date, fallback: Date) => {
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value
  }
  return fallback
}

export function roundUpToNextMinutes(input: Date, stepMinutes = 5) {
  const safeStep = normalizeScheduleStepMinutes(stepMinutes)
  const safeInput = resolveValidDate(input, new Date())
  const stepMs = safeStep * 60 * 1000
  const rounded = Math.ceil(safeInput.getTime() / stepMs) * stepMs
  return new Date(rounded)
}

export function getBrowserNowRounded(stepMinutes = 5, nowProvider?: () => Date) {
  const now = nowProvider ? nowProvider() : new Date()
  return roundUpToNextMinutes(now, stepMinutes)
}

export function coerceFutureDateTime(input: Date, stepMinutes = 5, nowProvider?: () => Date) {
  const roundedInput = roundUpToNextMinutes(input, stepMinutes)
  const roundedNow = getBrowserNowRounded(stepMinutes, nowProvider)
  if (roundedInput.getTime() < roundedNow.getTime()) {
    return roundedNow
  }
  return roundedInput
}
